import type { FC } from "react";
import { useMemo } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { LegendWithCheckboxAndFavicon } from "@similarweb/ui-components";

import { VerticalLegendsWithSearch } from "app/components/React/legend/vertical-legends-with-search/VerticalLegendsWithSearch";
import { SegmentLegendsWithFaviconAndCheckbox } from "app/components/React/Legends/SegmentLegendsWithFaviconAndCheckbox";
import { EEntityType } from "app/components/React/Table/cells/SegmentOrDomainCell";
import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { MAX_SELECTION } from "app/pages/industry-analysis/players/table/bubble-chart/const";
import {
    getBubbleChartCompareByIdMap,
    recordHasCompleteDataForBubbleChartType,
    sortLegendRecordsByConversionRateDataAvailability,
} from "app/pages/industry-analysis/players/table/bubble-chart/parseRawData";
import type { IRawData } from "app/pages/industry-analysis/players/table/types";
import { isLinesOfBusinessSegment } from "app/services/segments/LinesOfBusinessUtils";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";

import { ETypes, type SelectedEntitiesColorMapT } from "./types";

interface ILegends {
    rawData: IRawData;
    chartType: ETypes;
    selectedEntitiesIDs: Set<string>;
    onToggleEntity: (id: string) => void;
    selectedEntitiesColorMap: SelectedEntitiesColorMapT;
    width: string;
    isSegmentSupported: boolean;
    showAvgUniqueUsers?: boolean;
    isPeriodOverPeriod: boolean;
    trackOnLegendClick: (id: string, name: string, isChecked: boolean, position: number, data: number) => void;
}

const Container = styled.div<{ width: string }>`
    padding-top: 8px;
    width: ${({ width }) => width};
`;
export const Legends: FC<ILegends> = (props) => {
    const {
        rawData,
        chartType,
        selectedEntitiesIDs,
        onToggleEntity,
        trackOnLegendClick,
        selectedEntitiesColorMap,
        width,
        isSegmentSupported,
        showAvgUniqueUsers,
        isPeriodOverPeriod,
    } = props;

    const segmentsModule: ISegmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });
    const getUserAndAccountSegmentById = useMemo(
        () => SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule),
        [],
    );

    const compareByIdForBubbleChart = useMemo(
        () => (rawData ? getBubbleChartCompareByIdMap(rawData, isPeriodOverPeriod) : undefined),
        [rawData, isPeriodOverPeriod],
    );

    const orderedLegendData = useMemo(() => {
        if (!rawData?.Data?.length) {
            return [];
        }
        if (chartType !== ETypes.ConversionRate) {
            return rawData.Data;
        }
        return sortLegendRecordsByConversionRateDataAvailability(rawData.Data, compareByIdForBubbleChart);
    }, [rawData, chartType, compareByIdForBubbleChart]);

    if (!rawData) {
        return null;
    }

    const legendItems = orderedLegendData.map((dataItem, index) => {
        const {
            Id: id,
            Domain: name,
            DisplayName: displayName,
            Favicon: favicon,
            AvgMonthVisits: averageMonthlyVisits,
            Type: type,
            AvgUniqueUsers: averageUniqueVisits,
        } = dataItem;
        const isSelected = selectedEntitiesIDs.has(id);
        const isLobSegment = isLinesOfBusinessSegment(id);
        const segment = type === EEntityType.SEGMENT && !isLobSegment ? getUserAndAccountSegmentById(id) : undefined;
        const data = showAvgUniqueUsers ? averageUniqueVisits : averageMonthlyVisits;
        const isSegmentLike = !!(segment || isLobSegment);
        const isMissingConversionChartData =
            chartType === ETypes.ConversionRate &&
            !recordHasCompleteDataForBubbleChartType(dataItem, chartType, compareByIdForBubbleChart);
        const isDisabled = (isSegmentLike && !isSegmentSupported) || isMissingConversionChartData;

        let tooltip: string | null = null;
        if (isSegmentLike) {
            if (isLobSegment) {
                tooltip = translate("market.players.market.shares.chart.legend.tooltip.lob");
            } else if (chartType === ETypes.ConversionRate) {
                tooltip = translate("market.research.players.bubble.legends.tooltip.conversion.rate.domain.unavailable");
            } else {
                tooltip = translate("market.players.market.shares.chart.legend.tooltip.segment");
            }
        } else if (isMissingConversionChartData) {
            tooltip = translate("market.research.players.bubble.legends.tooltip.conversion.rate.domain.unavailable");
        }

        let legendName = name;
        let subtitleText: string | null = null;

        if (segment) {
            legendName = segment.domain;
            subtitleText = segment.segmentName;
        } else if (isLobSegment && displayName) {
            subtitleText = displayName;
        }

        return {
            visible: isSelected && !isDisabled,
            position: index + 1,
            name: legendName,
            subtitleText,
            id,
            averageMonthlyVisits,
            averageUniqueVisits,
            color: selectedEntitiesColorMap.get(id) || colorsPalettes.blue[400],
            data: minVisitsAbbrFilter()(data),
            favicon,
            customLegendComponent:
                segment || isLobSegment ? SegmentLegendsWithFaviconAndCheckbox : LegendWithCheckboxAndFavicon,
            isDisabled,
            setOpacity: isDisabled,
            tooltip,
        };
    });
    const toggleSeries = (legendItem) => {
        if (legendItem.isDisabled) {
            return;
        }
        const { id, position, averageMonthlyVisits } = legendItem;
        const isChecked = selectedEntitiesIDs.has(id);

        trackOnLegendClick(id, id, isChecked, position, averageMonthlyVisits);
        onToggleEntity(id);
    };
    return (
        <Container width={width}>
            <VerticalLegendsWithSearch
                legendItems={legendItems}
                legendItemsHeight={"293px"}
                maxSelection={MAX_SELECTION}
                text={{
                    searchPlaceHolder: translate("market.research.players.bubble.legends.search.placeholder"),
                    searchLabel: translate("market.research.players.bubble.legends.search.label"),
                }}
                toggleSeries={toggleSeries}
                textMaxWidth={"110px"}
                titles={[
                    translate("market.research.players.bubble.legends.titles.first"),
                    translate(
                        showAvgUniqueUsers
                            ? "market.research.players.bubble.legends.titles.second.unique"
                            : "market.research.players.bubble.legends.titles.second",
                    ),
                ]}
            />
        </Container>
    );
};
